import { useMemo } from 'react';
import { Line, LineConfig } from '@ant-design/plots';
import styled, { useTheme } from 'styled-components';

import { Box, ButtonIcon, Flex, Icons, Section, Typography } from 'components';

import { useBreakpoints } from 'app/hooks';
import { ProductJourneyDashboardRelationalMetricResponse } from 'features/journeys/types/ProductJourneyDashboardRelationalMetricResponse';
import { ProductJourneyRelationalMetricFormattingEnum } from 'features/journeys/types/ProductJourneyRelationalMetricFormattingEnum';
import { brazilianRealFormat } from 'app/utils';
import formatDateFromUtcToTimezone from 'app/utils/formatDateFromUtcToTimezone';

interface DataItem {
  period: string;
  type: string;
  value: number;
  color: string;
}

interface ChartData {
  data: DataItem[];
  colors: Record<string, string>;
}

interface MetricChartProps {
  metric: ProductJourneyDashboardRelationalMetricResponse;
  openRelationalMetricModal: (relationalMetricUuid: string, period?: number) => void;
  allowEditLatestRelationalMetricAssessment: boolean;
  clearPeriodValue: (period: number) => void;
}

const TooltipMetricButton = styled.button`
  border: 0;
  padding: 0;
  cursor: pointer;
`;

const MetricChart = ({ metric, openRelationalMetricModal, allowEditLatestRelationalMetricAssessment, clearPeriodValue }: MetricChartProps) => {
  const breakpoint = useBreakpoints();
  const { colors } = useTheme();

  const maxWidth = useMemo(() => {
    if (breakpoint === 'desktop') {
      return 1180;
    } else if (breakpoint === 'tablet') {
      return 580;
    }

    return 280;
  }, [breakpoint]);

  const chartData = useMemo<ChartData>(() => {
    // const periodicity = getPeriodicityOptions(metric.periodicity);

    return metric.expectations.reduce(
      (expectationPrev, expectation, expectationIndex) => {
        const values = expectation.values.reduce((valuePrev, value, valueIndex) => {
          return [
            ...valuePrev,
            {
              // period: `${valueIndex + 1}${periodicity.expectationValueSymbol} ${periodicity.expectationValueLabel}`,
              period: metric.periodAxisLegends[valueIndex],
              periodNumber: valueIndex + 1,
              type: expectation.expectationValueLegendText,
              value,
              color: expectation.expectationValueColor,
            },
          ];
        }, [] as DataItem[]);

        return {
          ...expectationPrev,
          data: [...expectationPrev.data, ...values],
          colors: { ...expectationPrev.colors, [expectation.expectationValueLegendText]: expectation.expectationValueColor },
        };
      },
      {
        data: metric.values.map((value, valueIndex) => ({
          period: metric.periodAxisLegends[valueIndex],
          periodNumber: valueIndex + 1,
          type: metric.assessmentValueLegendText,
          value: value,
          color: metric.assessmentValueColor,
        })),
        colors: { [metric?.assessmentValueLegendText]: metric?.assessmentValueColor },
      } as ChartData
    );
  }, [metric]);

  const config: LineConfig = {
    data: chartData.data,
    xField: 'period',
    yField: 'value',
    height: 350,
    yAxis: {
      label: {
        formatter: (v) => `${v}`.replace(/\d{1,3}(?=(\d{3})+$)/g, (s) => `${s},`),
      },
    },
    tooltip: {
      enterable: true,
      domStyles: {
        'g2-tooltip': {
          width: '260px',
          padding: 0,
          opacity: 1,
        },
      },
      customContent: (title, items) => {
        return (
          <Flex bg={colors.white} flexDirection="column" borderRadius={4}>
            <Flex
              justifyContent="space-between"
              alignItems="flex-start"
              p={{ m: 2 }}
              bg={colors.gray_100}
              borderTopLeftRadius={4}
              borderTopRightRadius={4}
            >
              <Flex flexDirection="column">
                <Typography type="type3" fontWeight="bold">
                  {title}
                </Typography>

                <Typography color={colors?.gray_500} type="type1" upper mt={1}>
                  Atualizado em: {formatDateFromUtcToTimezone(metric?.updatedAt).format('DD/MM/YYYY')}
                </Typography>
              </Flex>

              {metric?.period && items[0]?.data?.periodNumber <= metric?.period && (
                <Flex flexGrow={0} flexWrap="nowrap" style={{ gap: 2 }}>
                  <TooltipMetricButton title="Excluir valor" type="button" onClick={() => clearPeriodValue(items[0]?.data?.periodNumber)}>
                    <Icons color={colors.primary_400} icon="delete" size={0.7} />
                  </TooltipMetricButton>

                  <TooltipMetricButton
                    title="Editar valor"
                    type="button"
                    onClick={() => openRelationalMetricModal(metric?.uuid, items[0]?.data?.periodNumber)}
                  >
                    <Icons color={colors.primary_400} icon="edit" size={0.7} />
                  </TooltipMetricButton>
                </Flex>
              )}
            </Flex>

            <Flex p={{ m: 2 }} flexDirection="column">
              {items?.map((item, itemIndex) => {
                return (
                  <Flex key={`tooltip-${title}-${itemIndex.toString()}`} alignItems="flex-start" justifyContent="space-between" py="2px">
                    <Flex alignItems="flex-start" flexWrap="nowrap">
                      <div style={{ width: 10, height: 10, background: item?.data?.color, marginTop: 1, borderRadius: 2 }} />
                      <Typography type="type2" ml={1} upper color={colors?.gray_500}>
                        {item?.data?.type}
                      </Typography>
                    </Flex>

                    <Typography type="type2">
                      {metric?.formatting === ProductJourneyRelationalMetricFormattingEnum.Money
                        ? brazilianRealFormat((item?.data?.value as number).toFixed(2))
                        : item?.data?.value}
                    </Typography>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        );
      },
    },
    seriesField: 'type',
    color: ({ type }) => {
      return chartData.colors[type];
    },
    lineStyle: ({ type }) => {
      if (type === 'register') {
        return {
          lineDash: [4, 4],
          opacity: 1,
        };
      }

      return {
        opacity: 0.5,
      };
    },
    style: {
      maxWidth,
    },
  };

  return (
    <Flex bg="dark_350" pt={{ m: 8, d: 10 }}>
      <Section flexDirection="column">
        <Flex justifyContent={{ d: 'space-between' }} mb={4}>
          <Typography type="type4" fontWeight="bold" color="white" upper>
            {metric.name}
          </Typography>

          <Flex flexGrow={0} alignItems="center" mt={{ m: 2, d: 0 }}>
            <Typography type="type1" color="white50">
              Atualizado em: {formatDateFromUtcToTimezone(metric.updatedAt).format('DD/MM/YYYY [às] HH:mm')}
            </Typography>
          </Flex>
        </Flex>

        <Line {...config} />

        <Flex justifyContent={{ d: 'space-between' }} mt={5}>
          <Flex flexGrow={0} alignItems="flex-start" gridGap={{ m: 3, d: 6 }}>
            {metric.expectations?.map((expectation, expectationIndex) => {
              return (
                <Flex key={`metric-${metric.uuid}-${expectationIndex.toString()}`} flexDirection="column" justifyContent="flex-start">
                  <Box width="30px" height="3px" bg={expectation.expectationValueColor} />

                  <Typography type="type1" color="gray_500" mt={2} upper>
                    {expectation.expectationValueLegendText}
                  </Typography>

                  <Typography type="type4" color="white" fontWeight="bold" mt={1}>
                    {metric.formatting === ProductJourneyRelationalMetricFormattingEnum.Money
                      ? brazilianRealFormat(expectation.periodValue || 0)
                      : expectation.periodValue}
                  </Typography>
                </Flex>
              );
            })}
          </Flex>

          <Flex flexGrow={0} mt={{ m: 4, d: 0 }}>
            <Flex flexDirection="column" justifyContent="flex-start">
              <Box width="30px" height="3px" bg={metric.assessmentValueColor} />

              <Typography type="type1" color="gray_500" mt={2} upper>
                {metric.assessmentValueLegendText}
              </Typography>

              <Flex alignItems="center">
                <Typography type="type4" color="white" fontWeight="bold" mt={1}>
                  {metric.formatting === ProductJourneyRelationalMetricFormattingEnum.Money
                    ? brazilianRealFormat(metric.periodValue || 0)
                    : metric.periodValue}
                </Typography>

                {allowEditLatestRelationalMetricAssessment && (
                  <Flex flexGrow={0} ml={2}>
                    <ButtonIcon
                      onClick={() => openRelationalMetricModal(metric?.uuid)}
                      icon="edit"
                      iconWidth={16}
                      bg="transparent"
                      color="primary_400"
                      colorHover="primary_200"
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Section>
    </Flex>
  );
};

export default MetricChart;
